import { useFormState } from "@/lib/Components/Form/Hooks/useFormState";
import {
  RelocationAcknowledgementInput,
  RelocationFieldsFragment,
  UpdateRelocationInput,
} from "@/gql/graphql";
import {
  TrashIcon,
  PlusIcon,
  ClipboardDocumentCheckIcon,
} from "@heroicons/react/24/outline";
import { IconButton } from "@/lib/Components/Button/IconButton";
import { TextInput } from "@/lib/Components/Form/Inputs/TextInput";
import { TextAreaInput } from "@/lib/Components/Form/Inputs/TextAreaInput";
import { Button } from "@/components/catalyst/button";
import { useTranslations } from "use-intl";
import { RecordScreenFormCard } from "@/lib/Components/Screens/RecordScreen/Cards/RecordScreenFormCard";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { updateRelocationMutation } from "@/app/Relocations/GraphQL/relocationMutations";
import { useQueryClient } from "@tanstack/react-query";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { relocationAcknowledgementsValidation } from "@/app/Relocations/Validation/relocationValidation";

export function RelocationAcknowledgementsCard({
  relocation,
}: {
  relocation: RelocationFieldsFragment;
}) {
  const queryClient = useQueryClient();
  const { mutateAsync } = useGqlMutation(updateRelocationMutation, {
    onSuccess: () => {
      return queryClient.invalidateQueries();
    },
  });
  const t = useTranslations("relocation.acknowledgements");

  return (
    <RecordScreenFormCard<UpdateRelocationInput>
      containerClassName="rounded-lg border border-gray-200 bg-white p-6"
      title={
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-purple-100 p-2">
            <ClipboardDocumentCheckIcon className="h-5 w-5 text-purple-600" />
          </div>
          <div>
            <div>{t("title")}</div>
            <p className="text-sm font-normal text-gray-500">
              Required driver confirmations
            </p>
          </div>
        </div>
      }
      initialValues={{
        acknowledgements: {
          upsert:
            relocation.acknowledgements.length > 0
              ? relocation.acknowledgements.map((ack) => ({
                  id: ack.id,
                  name: ack.name,
                  acknowledgement: ack.acknowledgement,
                }))
              : [{ name: "", acknowledgement: "" }],
          delete: [],
        },
      }}
      validationSchema={toFormikValidationSchema(
        relocationAcknowledgementsValidation,
      )}
      onSubmit={async (values) => {
        await mutateAsync({
          id: relocation.id,
          input: values,
        });
      }}
      view={<AcknowledgementsViewList relocation={relocation} />}
    >
      <AcknowledgementsEditForm />
    </RecordScreenFormCard>
  );
}

function AcknowledgementsEditForm() {
  const { values, setFieldValue } = useFormState<UpdateRelocationInput>();
  const t = useTranslations("relocation.acknowledgements");

  function deleteValue(value: RelocationAcknowledgementInput, idx: number) {
    if (value.id) {
      setFieldValue("acknowledgements.delete", [
        ...(values.acknowledgements?.delete ?? []),
        value.id,
      ]);
    }

    setFieldValue(
      "acknowledgements.upsert",
      values.acknowledgements?.upsert?.filter((_, i) => i !== idx),
    );
  }

  function addAcknowledgement() {
    setFieldValue("acknowledgements.upsert", [
      ...(values.acknowledgements?.upsert ?? []),
      {
        name: "",
        acknowledgement: "",
      },
    ]);
  }

  return (
    <div className="col-span-full space-y-4">
      {values.acknowledgements?.upsert?.map((ack, idx) => (
        <div
          key={idx}
          className="relative rounded-lg border border-gray-200 bg-gray-50 p-4"
        >
          <div className="absolute right-2 top-2">
            <IconButton
              Icon={TrashIcon}
              tooltip={t("delete")}
              onClick={() => deleteValue(ack, idx)}
            />
          </div>

          <div className="grid gap-4 pr-10">
            <TextInput
              name={`acknowledgements.upsert.${idx}.name`}
              label={t("nameLabel")}
            />
            <TextAreaInput
              name={`acknowledgements.upsert.${idx}.acknowledgement`}
              label={t("textLabel")}
            />
          </div>
        </div>
      ))}

      <Button type="button" outline onClick={addAcknowledgement}>
        <PlusIcon />
        {t("add")}
      </Button>
    </div>
  );
}

function AcknowledgementsViewList({
  relocation,
}: {
  relocation: RelocationFieldsFragment;
}) {
  const t = useTranslations("relocation.acknowledgements");

  if (relocation.acknowledgements.length === 0) {
    return (
      <p className="text-sm italic text-gray-500">{t("noAcknowledgements")}</p>
    );
  }

  return (
    <ul className="space-y-2">
      {relocation.acknowledgements.map((ack) => (
        <li
          key={ack.id}
          className="rounded-lg border border-gray-200 bg-gray-50 p-3"
        >
          <div className="font-medium text-gray-900">{ack.name}</div>
          <div className="mt-1 text-sm text-gray-600">
            {ack.acknowledgement}
          </div>
        </li>
      ))}
    </ul>
  );
}
